import type { ControlCenterJob, ControlCenterPerson, ControlCenterPersonKind } from '../types/controlCenter'
import { normalizeLatLng } from './geoCoordinates'

function asPersonKind(value: unknown): ControlCenterPersonKind {
  return String(value || '').toLowerCase() === 'employee' ? 'employee' : 'external'
}

/**
 * Map a `jobtread.listPeople` row to a Control Center person entity.
 */
export function mapJobtreadPersonRow(row: Record<string, unknown>): ControlCenterPerson | null {
  const id = String(row.id ?? '').trim()
  if (!id) return null

  const clockedIn = Boolean(row.clockedIn)
  const geo = clockedIn ? normalizeLatLng(row.lat, row.lng) : null

  return {
    id,
    name: String(row.name || '').trim() || 'Unknown',
    email: row.email != null && String(row.email).trim() ? String(row.email).trim() : null,
    phone: row.phone != null && String(row.phone).trim() ? String(row.phone).trim() : null,
    roleName:
      row.roleName != null && String(row.roleName).trim() ? String(row.roleName).trim() : null,
    personKind: asPersonKind(row.personKind),
    employeeId:
      row.employeeId != null && String(row.employeeId).trim()
        ? String(row.employeeId).trim()
        : null,
    portalName:
      row.portalName != null && String(row.portalName).trim()
        ? String(row.portalName).trim()
        : null,
    hasPortalUser: Boolean(row.hasPortalUser),
    clockedIn,
    timeEntryId:
      row.timeEntryId != null && String(row.timeEntryId).trim()
        ? String(row.timeEntryId).trim()
        : null,
    startedAt:
      row.startedAt != null && String(row.startedAt).trim() ? String(row.startedAt) : null,
    jobId: row.jobId != null && String(row.jobId).trim() ? String(row.jobId).trim() : null,
    jobName: row.jobName != null && String(row.jobName).trim() ? String(row.jobName).trim() : null,
    jobNumber:
      row.jobNumber != null && String(row.jobNumber).trim() ? String(row.jobNumber).trim() : null,
    locationId:
      row.locationId != null && String(row.locationId).trim()
        ? String(row.locationId).trim()
        : null,
    locationName:
      row.locationName != null && String(row.locationName).trim()
        ? String(row.locationName).trim()
        : null,
    address: row.address != null && String(row.address).trim() ? String(row.address).trim() : null,
    lat: geo?.lat ?? null,
    lng: geo?.lng ?? null,
  }
}

/**
 * Fill missing clock-in coordinates from the job's known location.
 * JobTread time entries sometimes omit nested location lat/lng.
 */
export function enrichPeopleWithJobLocations(
  people: ControlCenterPerson[],
  jobs: ControlCenterJob[],
): ControlCenterPerson[] {
  if (people.length === 0 || jobs.length === 0) return people
  const jobsById = new Map(jobs.map((job) => [job.id, job]))
  const jobsByLocationId = new Map(
    jobs.filter((job) => job.locationId).map((job) => [job.locationId, job]),
  )

  return people.map((person) => {
    if (!person.clockedIn) return person

    const existing = normalizeLatLng(person.lat, person.lng)
    const job =
      (person.jobId ? jobsById.get(person.jobId) : undefined) ||
      (person.locationId ? jobsByLocationId.get(person.locationId) : undefined)

    if (existing) {
      const normalized =
        person.lat !== existing.lat || person.lng !== existing.lng
          ? { ...person, lat: existing.lat, lng: existing.lng }
          : person
      if (normalized.address || !job) return normalized
      return {
        ...normalized,
        address: job.address || normalized.address,
        locationId: normalized.locationId || job.locationId || null,
        locationName: normalized.locationName || job.locationName || null,
      }
    }

    if (!job) return person
    const jobGeo = normalizeLatLng(job.lat, job.lng)
    if (!jobGeo) return person
    return {
      ...person,
      lat: jobGeo.lat,
      lng: jobGeo.lng,
      address: person.address || job.address || null,
      locationId: person.locationId || job.locationId || null,
      locationName: person.locationName || job.locationName || null,
    }
  })
}
